import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { isMultiSeries, deriveSeries } from "./chartSeries";
import { formatPeriodLabel } from "./periodLabel.mjs";

const DEFAULT_HEIGHT = 320;

// The lab's own categorical palette — not sq-ui's. The product keys its palette by SERIES
// COUNT and it lives in that package; this one is a flat list indexed by position.
//
// SIXTEEN, and the count is measured rather than picked: at eight, segment 9 repeated segment
// 1 — three tighter than the product's own known collision at 11 — and an 11-segment site axis
// is reachable on a live account today. Past sixteen it still wraps and two segments share a
// colour; the engine's crowded-chart line covers a stacked bar above six segments only, so a
// seventeen-slice pie gets no warning. That gap is named, not closed here.
const PALETTE = [
  "#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#ef4444",
  "#0f766e", "#b45309", "#7c3aed", "#be123c", "#0369a1", "#4d7c0f", "#a21caf", "#57534e",
];

// The single-series stroke/fill a bar and a line have always used. Named so the per-point
// fallback below cannot drift away from the colour an uncoloured chart still renders.
const SERIES_COLOR = "#2563eb";

// A per-point colour the ENGINE supplied, or nothing.
//
// attachStoredPointColors (packages/engine/llm-agent/loop.js) puts a rating band's own stored
// hex on each point — the same column the dashboards' Final Rating donut paints from. Without
// this read it was inert here: a rating breakdown rendered green/red in the product and generic
// blue in the lab, which is the page the demo runs from.
//
// ★ ONE DIMENSION TAKES THE ACCOUNT'S OWN COLOURS; TWO TAKE THE PALETTE. On a stacked or
//   grouped chart the colour IS the series identity, so painting it by label would recolour a
//   series and mislead — which is why the engine's attach is single-series gated and why the
//   multi-series branches below read no colour. That is intended, not a gap.
//
// Re-validated here rather than trusted: the value is tenant-editable text on its way to an
// SVG `fill`, and it crosses a socket between the engine's check and this one.
const POINT_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function pointColor(point) {
  return point && typeof point.color === "string" && POINT_COLOR.test(point.color) ? point.color : null;
}

const hasPointColors = (data) => data.some((p) => pointColor(p) !== null);

// EVERY CHART LABEL GOES THROUGH ONE FUNCTION — the axis tick, the tooltip heading, the series
// name in the tooltip and the legend. The engine emits a period KEY (`2026-03`) and the product
// renders a month NAME, so the key is formatted HERE, at render, exactly where the product's own
// AI page formats it; the spec's data is untouched, so sorting and the captured PNG/PDF caption
// still read the key's order. formatPeriodLabel returns a non-month label BYTE-IDENTICAL, which
// is what makes it safe on a legend showing "Raised" or an axis showing a site name.
//
// The `table` branch below is DELIBERATELY NOT one of these sites: a chart axis is not a date in
// a table, and this page's table format is owned by resultDates.mjs and its own field enum.
const axisLabel = formatPeriodLabel;

const AXIS_STYLE = { fill: "#6b7280", fontSize: 12, fontFamily: "Inter" };
const GRID_STYLE = { stroke: "#f3f4f6" };

// K-QF13-4: the product's AI page writes a figure with the browser's toLocaleString (ChartBlock numberFormat); pinned to en-US so a
// browser locale cannot move the grouping or the decimal point.
function formatNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return String(value);
  return value.toLocaleString("en-US");
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md">
      {label !== undefined && <div className="text-xs font-medium text-gray-700 mb-1">{axisLabel(label)}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          {/* THE POINT'S COLOUR, THEN THE SERIES'. `entry.color` is what Recharts assigned the
              SERIES — measured as #2563eb on a bar whose own rectangle paints #388117 — so a
              swatch reading it alone disagrees with the mark it is labelling the moment a point
              carries a stored colour. `entry.payload` is the data point, which is where the
              engine put it; on a multi-series chart that payload is the pivoted row and carries
              no colour, so the series colour is still what shows. */}
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: pointColor(entry.payload) || entry.color }} />
          <span className="text-gray-500">{axisLabel(entry.name)}:</span>
          <span className="font-medium text-gray-900">{formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ChartRenderer({ spec }) {
  const { chart_type, data, x_axis_label, y_axis_label } = spec;

  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-400 italic py-8 text-center">No data to display.</div>;
  }

  switch (chart_type) {
    case "kpi": {
      const row = data[0];
      const value = row.value;
      const formatted = formatNumber(value);
      const hasComparison = row.comparison_value !== undefined && row.comparison_value !== null;
      let delta = null;
      let deltaColor = "";
      if (hasComparison && typeof value === "number" && typeof row.comparison_value === "number") {
        const change = value - row.comparison_value;
        const pct = row.comparison_value !== 0 ? (change / row.comparison_value) * 100 : null;
        if (pct !== null) {
          delta = `${change >= 0 ? "+" : ""}${formatNumber(change)} (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)`;
          deltaColor = change > 0 ? "text-emerald-600" : change < 0 ? "text-red-600" : "text-gray-500";
        }
      }
      return (
        <div className="text-center py-4">
          <div className="text-5xl font-semibold text-gray-900 tracking-tight tabular-nums">{formatted}</div>
          {hasComparison && delta && (
            <div className={`text-sm font-medium mt-2 ${deltaColor}`}>
              {delta}
              {row.comparison_label && <span className="text-gray-400 font-normal ml-1.5">{row.comparison_label}</span>}
            </div>
          )}
        </div>
      );
    }

    case "bar": {
      if (isMultiSeries(data)) {
        const { segments, dataset } = deriveSeries(data);
        return (
          <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
            <BarChart data={dataset} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="label" tickFormatter={axisLabel} tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
              <Legend formatter={axisLabel} wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
              {segments.map((seg, i) => (
                <Bar key={seg} dataKey={seg} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: x_axis_label ? 24 : 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tickFormatter={axisLabel} tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false}
              label={x_axis_label ? { value: x_axis_label, position: "insideBottom", offset: -10, style: AXIS_STYLE } : undefined} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false}
              label={y_axis_label ? { value: y_axis_label, angle: -90, position: "insideLeft", style: AXIS_STYLE } : undefined}
              tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
            <Bar dataKey="value" fill={SERIES_COLOR} radius={[4, 4, 0, 0]}>
              {/* Cells ONLY where the engine actually sent a colour. With none, the Bar keeps
                  its own single fill and no Cell is rendered at all, so an uncoloured chart is
                  the DOM it was before — which is most of them (a month, a site, a template). */}
              {hasPointColors(data)
                && data.map((entry, i) => <Cell key={i} fill={pointColor(entry) || SERIES_COLOR} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    case "line": {
      if (isMultiSeries(data)) {
        const { segments, dataset } = deriveSeries(data);
        return (
          <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
            <LineChart data={dataset} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="label" tickFormatter={axisLabel} tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
              <Tooltip content={<CustomTooltip />} />
              <Legend formatter={axisLabel} wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
              {segments.map((seg, i) => (
                <Line key={seg} type="monotone" dataKey={seg} stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2} dot={{ fill: PALETTE[i % PALETTE.length], r: 3 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tickFormatter={axisLabel} tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            {/* No per-point read: a line is one stroke, so a colour per point reads as noise —
                the engine excludes `line` from its colourable types for the same reason. */}
            <Line type="monotone" dataKey="value" stroke={SERIES_COLOR} strokeWidth={2} dot={{ fill: SERIES_COLOR, r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    case "pie":
    case "donut":
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="label"
              cx="50%" cy="50%"
              outerRadius={chart_type === "donut" ? 110 : 120}
              innerRadius={chart_type === "donut" ? 65 : 0}
              paddingAngle={1}
              labelLine={false}
              label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={pointColor(entry) || PALETTE[i % PALETTE.length]} stroke="white" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={axisLabel} wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
          </PieChart>
        </ResponsiveContainer>
      );

    case "stacked_bar": {
      const { segments, dataset } = deriveSeries(data);
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <BarChart data={dataset} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tickFormatter={axisLabel} tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
            <Legend formatter={axisLabel} wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
            {segments.map((seg, i) => (
              <Bar key={seg} dataKey={seg} stackId="a" fill={PALETTE[i % PALETTE.length]} radius={i === segments.length - 1 ? [4, 4, 0, 0] : 0} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    case "table": {
      // THE AXIS LABELS THE ENGINE RESOLVED, NOT THE WIRE KEYS. `Object.keys(data[0])` put
      // LABEL / VALUE / SEGMENT above the columns — the field-name leak the prose path was
      // cleaned of, in a table header, on the demo surface. A `table` spec is the same flat
      // {label, value, segment} row set as every other chart type, so the three columns are
      // known; this is the product's own table branch (specToHighcharts) in Recharts' place.
      const hasSegment = data.some((d) => d.segment !== null && d.segment !== undefined);
      const columns = [
        { key: "label", header: x_axis_label || "Label" },
        ...(hasSegment ? [{ key: "segment", header: "Series" }] : []),
        { key: "value", header: y_axis_label || "Value" },
      ];
      return (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                {columns.map((c) => (
                  <th key={c.key} className="px-3 py-2.5 text-left font-medium text-gray-700 text-xs uppercase tracking-wider">{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                  {columns.map((c) => {
                    const val = row[c.key];
                    const display = val === null || val === undefined ? "—"
                                  : typeof val === "number" ? formatNumber(val)
                                  : typeof val === "object" ? JSON.stringify(val)
                                  : String(val);
                    return (
                      <td key={c.key} className={`px-3 py-2.5 text-gray-900 ${typeof val === "number" ? "tabular-nums text-right" : ""}`}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    default:
      return <div className="text-sm text-red-600">Unsupported chart type: {chart_type}</div>;
  }
}
